import { useGetInventoryEnvironmentOptions } from "@/api/useInventory"
import { SEVERITIES_FILTER_OPTIONS } from "@/const/options.constants"
import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import { TEASERS } from "@/const/teaser.constants"
import { isDataAvailable } from "@/helpers/context.helper"
import { useInitialRender } from "@/hooks/useInitialRender"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useOrderBy } from "@/hooks/useOrderBy"
import { useScopesPresentation } from "@/hooks/useScopesPresentation"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ScanProgressEmptyTable } from "@/molecules/EmptyTable"
import { DatePickerExportContainer } from "@/organisms/DatePickerExportContainer"
import { APPLICATION_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { Filter } from "@/organisms/FilterNew/Filter"
import { InputSmartSearchLoading, SmartSearch } from "@/organisms/SmartSearch"
import { useApplicationInventory } from "@/pages/DiscoveryPage/application/application-inventory/useApplicationInventory"
import { AsideFooter } from "@/pages/DiscoveryPage/components/molecules/AsideFooter"
import { showScanningProgressImage } from "@/pages/DiscoveryPage/utils/table.utils"
import { TabTemplate } from "@/templates/TabTemplate"
import { getColumnsTable } from "./getColumnsTable"
import {
  getFilters,
  getWorkloadStatusOptions,
  getWorkloadTypeOptions
} from "./getItemsFilter"

import type { ApplicationTabProps } from "."

export const ApplicationsTab: React.FC<ApplicationTabProps> = ({
  useActiveKeyTabsInventory
}) => {
  const TAB_KEYS = TAB_KEYS_BY_PAGE.INVENTORY
  const { activeKey, config = {} } = useActiveKeyTabsInventory()
  const previewMode = !!config.headers
  const featureFlags = useFeatureFlags()
  const internetFacingFF = featureFlags.internetFacingIntegration
  const { t } = useTranslation()

  const { environmentOptions, isLoading: isLoadingEnvironments } =
    useGetInventoryEnvironmentOptions()

  const filterItems = getFilters(t, {
    internetFacingFF,
    environmentOptions
  })
  const convertors = useNumberConvertor()
  const { onSorterChange, ...orderByProps } = useOrderBy()
  const { idsInDrawers, onRowClick } = useDrawer(APPLICATION_DRAWER_ID, {
    filters: ["severity", "riskId", "runtime"]
  })

  const columns = getColumnsTable({
    internetFacingFF,
    previewMode,
    convertors,
    sorter: orderByProps,
    t,
    onRowClick
  })

  const context = useInitialRender(
    useApplicationInventory(filterItems, {
      enabled: activeKey === TAB_KEYS[1],
      ...config
    })
  )
  const { response, isLoading } = context
  const { data, metadata } = response || {}

  const dataReadinessFF = featureFlags.dataReadiness
  const filterDisabled =
    !!dataReadinessFF || internetFacingFF === undefined || isLoadingEnvironments
  const noData = !isDataAvailable(context)
  const disableExport = noData || filterDisabled
  const placement = "applications-page"
  const { isAllResourceScope } = useScopesPresentation()
  const showScanningProgress =
    noData && showScanningProgressImage(featureFlags) && isAllResourceScope

  useTooltip(data)

  return (
    <TabTemplate
      resizable
      dataSource={data}
      metadata={metadata}
      columns={columns}
      loading={isLoading}
      locale={{
        customEmptyComponent: showScanningProgress && (
          <ScanProgressEmptyTable isLoading={isLoading} />
        ),
        teasers: {
          dataReadiness: true,
          kortexInstalled: TEASERS.KORTEX_INSTALLED.noActiveSensorsDeployed(t)
        }
      }}
      idsInDrawers={idsInDrawers}
      asideFooter={
        <AsideFooter label={t("inventory.application.terminatedRetention")} />
      }
      onRowClick={onRowClick}
      disableDrawer={previewMode}
      SearchComponent={
        !previewMode && (
          <Filter.Provider keys={Object.keys(filterItems)}>
            <Filter.Template
              isLoading={filterDisabled}
              aside={
                <DatePickerExportContainer
                  isLoading={isLoading}
                  disableExport={disableExport}
                  exportType="Application"
                  placement={placement}
                />
              }
              search={
                filterDisabled ? (
                  <InputSmartSearchLoading />
                ) : (
                  <SmartSearch
                    id="smart-search-application-tab-inventory"
                    filters={filterItems}
                    searchedVulnerabilities={metadata?.searchedVulnerabilities}
                  />
                )
              }
            >
              <Filter.Checkbox
                isLoading={filterDisabled}
                keyFilter="workloadStatus"
                items={getWorkloadStatusOptions(t)}
                placeHolderDefaultValue="Status"
                placement={placement}
              />
              <Filter.Checkbox
                isLoading={filterDisabled}
                keyFilter="workloadType"
                items={getWorkloadTypeOptions()}
                placeHolderDefaultValue="Workload Type"
                placement={placement}
              />
              <Filter.Checkbox
                isLoading={filterDisabled}
                order="none"
                keyFilter="severity"
                items={SEVERITIES_FILTER_OPTIONS}
                placeHolderDefaultValue="Severity"
                placement={placement}
              />
              <Filter.Insights
                filters={filterItems}
                isLoading={filterDisabled}
                placement={placement}
              />
            </Filter.Template>
          </Filter.Provider>
        )
      }
      onChange={(_pagination, _filters, sorter) => {
        onSorterChange(sorter)
      }}
    />
  )
}
